<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import {
  useBrowserStore,
  usePaxStore,
} from '@apps/main/new-desktop/core/stores';

import { isExperimentAsync, isFeatureToggleActive } from '@libs/feature-toggle';

import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripSummaryStore,
} from '../../stores';
import TripOverviewHeader from '../tripOverviewHeader/TripOverviewHeader.vue';

import TripSummaryColumnMobileLoader from './TripSummaryColumnMobileLoader.vue';
import TripSummaryColumnFooter from './TripSummaryColumnFooter.vue';
import TripPriceSummary from './TripPriceSummary.vue';

const tripSummaryStore = useTripSummaryStore();
const paxStore = usePaxStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();

const shouldShowTripSummaryPreCheckout = computedAsync<boolean>(
  () => isExperimentAsync('trip_summary_pre_checkout_experiment'),
  false
);
</script>

<template>
  <div class="trip-summary-column">
    <template v-if="browserStore.isLargerThan1199px">
      <TripOverviewHeader />
      <TripSummaryColumnFooter
        :paxes="paxStore.getPaxesButtonSimpleText"
        :travel-dates="tripSummaryStore.travelDateRange"
        :total-price="tripSummaryStore.tripTotalPrice"
        :gross-price="tripSummaryStore.tripGrossPrice"
        :total-addons-price="tripSummaryStore.tripTotalAddonsPrice"
        :currency="tripSummaryStore.tripSummaryCurrency"
        :should-show-share-button="true"
        class="footer"
        @total-price-clicked="tripOverviewModalsStore.openTripPriceSummaryModal"
      />
    </template>

    <template v-else>
      <template
        v-if="
          tripOverviewPageStore.anyItineraryLoaded &&
          !isFeatureToggleActive('loader')
        "
      >
        <div>
          <TripPriceSummary
            v-if="!shouldShowTripSummaryPreCheckout"
            class="trip-summary-price-summary"
            :price-segments="tripSummaryStore.tripPriceSegments"
          />

          <TripSummaryColumnFooter
            :paxes="paxStore.getPaxesButtonSimpleText"
            :travel-dates="tripSummaryStore.travelDateRange"
            :currency="tripSummaryStore.tripSummaryCurrency"
            :total-price="tripSummaryStore.tripTotalPrice"
            :gross-price="tripSummaryStore.tripGrossPrice"
            :total-addons-price="tripSummaryStore.tripTotalAddonsPrice"
            :should-show-share-button="true"
          />
        </div>
      </template>
      <template v-else>
        <TripSummaryColumnMobileLoader />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-column {
  height: auto;
  background-color: var(--color-white);
  border: 1px solid var(--color-neutral-30);
  border-radius: var(--border-radius-8);

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--spacing-12);
  padding: var(--spacing-24);

  @include for-less-or-equal-than-1199-px {
    box-shadow: var(--shadow-elevation-200);
    border-radius: 0;
    gap: 0;
    padding: 0;
  }

  .trip-summary-price-summary {
    padding: var(--spacing-16) var(--spacing-8) 0 var(--spacing-8);

    @include for-less-or-equal-than-1199-px {
      padding: var(--spacing-24) var(--spacing-16) 0 var(--spacing-16);
    }
  }

  .footer {
    @include for-less-or-equal-than-1199-px {
      padding: var(--spacing-12) var(--spacing-24) var(--spacing-24)
        var(--spacing-24);
    }
  }
}
</style>
